import { useState } from "react"

import { cn } from "@/lib/utils"

/**
 * A funnel bar you can click to see who it includes.
 *
 * Lives beside `NameList` for the same reason: more than one dashboard asks a
 * bar the same question. The onboarding funnel asks who stopped here, and the
 * iMessage funnel asks who is still waiting on us — a count neither one can
 * turn into a call list is a count nobody acts on.
 */
export function NamedBar({
  label,
  value,
  total,
  color,
  caption,
  unknown,
  faded,
  rest,
  sub,
  hover,
  flyout,
}: {
  label: string
  value: number
  total: number
  color: string
  caption?: React.ReactNode
  /** Nothing could answer this step. Draws an em dash and no bar, because a
   *  bold `0` beside bars that DID get answered reads as "nobody", and the
   *  caption alone never wins that argument. */
  unknown?: boolean
  /** Part of `value` drawn washed-out at the tail — e.g. people we can't name. */
  faded?: number
  /** The remainder of `value` in its OWN colour — e.g. who did not continue.
   *  A green-then-red bar says "completed / stopped" without a legend; the
   *  diagonal-hatch trick it replaces did not read as anything to anyone. */
  rest?: { value: number; color: string }
  /** A full-width line under the bar, for a rate the bar itself can't carry. */
  sub?: React.ReactNode
  hover: React.ReactNode
  /** Hover panel — used for the paywall name × views list. */
  flyout?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const solid = unknown ? 0 : Math.max(0, value - (faded ?? 0) - (rest?.value ?? 0))
  const pctOf = (n: number) => (total > 0 ? (n / total) * 100 : 0)
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full text-left cursor-pointer"
      >
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-base font-medium w-52 shrink-0 truncate">{label}</span>
          <span className="text-lg font-semibold tabular-nums">
            {unknown ? "\u2014" : value}
          </span>
          {caption && (
            <span className="text-base text-muted-foreground truncate">{caption}</span>
          )}
          <span className="ml-auto text-base text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100">
            {open ? "hide names" : "show names"}
          </span>
        </div>
        <div
          className={cn(
            "h-8 rounded bg-muted/50 overflow-hidden ring-offset-1 flex",
            open ? "ring-2 ring-ring" : "group-hover:ring-1 group-hover:ring-border",
          )}
        >
          <div
            className="h-full transition-all"
            style={{
              width: `${Math.max(pctOf(solid), solid > 0 ? 1.5 : 0)}%`,
              background: color,
            }}
          />
          {!!rest?.value && (
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.max(pctOf(rest.value), 1.5)}%`,
                background: rest.color,
              }}
            />
          )}
          {!!faded && (
            <div
              className="h-full transition-all opacity-25"
              style={{ width: `${Math.max(pctOf(faded), 1.5)}%`, background: color }}
            />
          )}
        </div>
        {sub && (
          <div className="text-base text-muted-foreground mt-1">{sub}</div>
        )}
      </button>
      {flyout && (
        <div className="pointer-events-none absolute left-0 sm:left-52 top-0 z-30 w-80 max-h-72 overflow-y-auto rounded-lg border bg-popover p-3 shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
          {flyout}
        </div>
      )}
      {open && (
        <div className="mt-2 rounded-lg border bg-muted/30 p-3">{hover}</div>
      )}
    </div>
  )
}
